import React, { useState, useRef, useContext } from 'react';
import { isUserOnline } from '../utils';
import { useAuth } from '../AuthContext';
import { usePuzzles } from '../PuzzleContext';
import { Comment, Page, PageContextType, Timestamp } from '../types';
import { Timestamp as TimestampValue } from '../firebase';
import { useAdmin } from '../AdminContext';
import InteractionMenu from './InteractionMenu';
import { PageContext } from '../PageContext';
import GiphyPicker from './GiphyPicker';

interface CommentSectionProps {
    puzzleId: string;
    initialComments: Comment[];
    answer: string;
    creatorId: string;
    onClick: (e: React.MouseEvent) => void;
}

const TaggedContent: React.FC<{ text: string; userId: string; baseClassName?: string; }> = ({ text, userId, baseClassName }) => {
    const { allUsers } = useAuth();
    const pageContext = useContext(PageContext);
    const commenter = allUsers.find(u => u.uid === userId);
    const isBusiness = commenter?.isBusiness || false;

    const handleTagClick = (e: React.MouseEvent, type: 'mention' | 'hashtag', value: string) => {
        e.stopPropagation();
        if (type === 'hashtag') {
            pageContext?.switchPage(Page.FilteredFeed, value, { hashtag: value });
        } else if (type === 'mention') {
            const mentionedUser = allUsers.find(u => u.displayName.toLowerCase() === value.toLowerCase());
            if (mentionedUser) {
                pageContext?.switchPage(Page.Profile, mentionedUser.displayName, { userId: mentionedUser.uid });
            }
        }
    };

    // Regex for hashtags, mentions, and URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const mentionRegex = /(@[a-zA-Z0-9_]+)/g;
    const hashtagRegex = /(#[a-zA-Z0-9_]+)/g;
    
    const parts = text.split(/(https?:\/\/[^\s]+|#[a-zA-Z0-9_]+|@[a-zA-Z0-9_]+)/g);

    return (
        <p className={`${baseClassName || ''} select-none`} style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}>
            {parts.map((part, i) => {
                if (part.startsWith('http')) {
                    if (isBusiness) {
                        return (
                            <a 
                                key={i} 
                                href={part} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-400 hover:underline font-semibold"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {part}
                            </a>
                        );
                    }
                    return <span key={i} className="opacity-70 italic">{part}</span>;
                }
                if (part.startsWith('#')) {
                    return (
                        <button key={i} onClick={(e) => handleTagClick(e, 'hashtag', part)} className="font-semibold text-blue-400 hover:underline focus:outline-none">
                            {part}
                        </button>
                    );
                }
                if (part.startsWith('@')) {
                    const username = part.substring(1);
                    return (
                        <button key={i} onClick={(e) => handleTagClick(e, 'mention', username)} className="font-semibold text-pink-400 hover:underline focus:outline-none">
                            {part}
                        </button>
                    );
                }
                return <span key={i}>{part}</span>;
            })}
        </p>
    );
};

const CommentSection: React.FC<CommentSectionProps> = ({ puzzleId, initialComments, answer, creatorId, onClick }) => {
    const { userProfile, allUsers } = useAuth();
    const { addCommentToPuzzle, deleteComment, addReactionToComment } = usePuzzles();
    const { reportUser } = useAdmin();
    const [newComment, setNewComment] = useState('');
    const [showSpoilers, setShowSpoilers] = useState(false);
    const [showGiphy, setShowGiphy] = useState(false);

    // Interaction Menu State
    const [activeComment, setActiveComment] = useState<Comment | null>(null);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);


    const [replyingTo, setReplyingTo] = useState<Comment | null>(null);

    const handleSubmitComment = (e?: React.FormEvent, imageUrl?: string) => {
        if (e) e.preventDefault();
        if (!newComment.trim() && !imageUrl && !userProfile) return;
        if (!userProfile) return;

        let textToSubmit = newComment;
        
        const clean = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
        const cleanAnswer = clean(answer);
        const cleanComment = clean(newComment);

        if (cleanComment.includes(cleanAnswer) && cleanAnswer.length > 1) { 
            const regex = new RegExp(answer, 'gi');
            const replaced = newComment.replace(regex, '****');
            textToSubmit = replaced !== newComment ? replaced : "****";
        }
        
        // If replying to an existing reply, tag them and keep the thread 1-level deep
        let finalReplyToId = replyingTo ? replyingTo.id : undefined;
        let replyingToUserIdParam = undefined;
        if (replyingTo && replyingTo.replyToId) {
             finalReplyToId = replyingTo.replyToId; // Inherit parent thread ID
             textToSubmit = `@${replyingTo.username || replyingTo.userDisplayName.replace(/\s+/g, '')} ${textToSubmit}`;
             replyingToUserIdParam = replyingTo.userId;
        } else if (replyingTo) {
             replyingToUserIdParam = replyingTo.userId;
        }

        addCommentToPuzzle(puzzleId, {
            userId: userProfile.uid,
            userDisplayName: userProfile.displayName,
            userPhotoURL: userProfile.photoURL,
            text: textToSubmit,
            imageUrl: imageUrl || undefined,
            createdAt: TimestampValue.now(),
            replyToId: finalReplyToId,
            replyingToUserId: replyingToUserIdParam,
        });

        setNewComment('');
        setShowGiphy(false);
        setReplyingTo(null);
    };
    
    // --- Long Press Logic ---
    const handlePressStart = (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>, comment: Comment) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();

        longPressTimer.current = setTimeout(() => {
            setActiveComment(comment);
            setMenuPosition({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height
            });
            longPressTimer.current = null;
            if (navigator.vibrate) navigator.vibrate(50);
        }, 500);
    };

    const handlePressEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const handleReaction = (emoji: string) => {
        if (activeComment) {
            addReactionToComment(puzzleId, activeComment.id, emoji);
        }
    };

    const handleAction = async (action: 'reply' | 'copy' | 'share' | 'report' | 'delete') => {
        if (!activeComment) return;

        switch(action) {
            case 'reply':
                setNewMessage(`@${activeComment.userDisplayName} `);
                break;
            case 'copy':
                navigator.clipboard.writeText(activeComment.text);
                alert("Comment copied");
                break;
            case 'share':
                const textToShare = `${activeComment.userDisplayName} commented: "${activeComment.text}" on What Was It?`;
                if (navigator.share) {
                    try {
                        await navigator.share({
                            title: 'Comment from What Was It?',
                            text: textToShare,
                        });
                    } catch (err) {
                        console.error('Error sharing:', err);
                    }
                } else {
                    navigator.clipboard.writeText(textToShare);
                    alert('Comment copied to clipboard for sharing!');
                }
                break;
            case 'report':
                if(window.confirm("Report this comment for violating rules?")) {
                    alert("Report submitted. Thanks for keeping our community safe.");
                }
                break;
            case 'delete':
                if (window.confirm("Delete this comment?")) {
                    await deleteComment(puzzleId, activeComment.id, initialComments);
                }
                break;
        }
    };

    const setNewMessage = (text: string) => {
        setNewComment(text);
        const input = document.querySelector('input[placeholder*="Add a comment"]') as HTMLInputElement;
        if (input) input.focus();
    };

    // Helper to check for spoilers client-side (for display of older comments)
    const isContentSpoiler = (text: string) => {
        const clean = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
        const cleanAns = clean(answer);
        return cleanAns.length > 1 && clean(text).includes(cleanAns);
    };

    return (
        <div className="p-4 bg-black/20" onClick={onClick}>
            {userProfile && (
                <div className="mb-4">
                    <form onSubmit={(e) => handleSubmitComment(e)} className="flex items-center space-x-2">
                        <img src={userProfile.photoURL} alt="Your avatar" className="w-8 h-8 rounded-full" />
                        <button 
                            type="button"
                            onClick={() => setShowGiphy(true)}
                            className="text-gray-400 hover:text-purple-500 transition-colors p-1"
                            title="Add GIF"
                        >
                            <i className="ph-duotone ph-gif text-2xl"></i>
                        </button>
                        <div className="flex-1 relative border border-white/20 rounded-xl overflow-hidden focus-within:border-white/50 focus-within:bg-white/10 transition-colors">
                            {replyingTo && (
                                <div className="flex items-center justify-between px-3 py-1.5 bg-white/5 border-b border-white/10 text-xs text-gray-300">
                                    <span>Replying to <span className="font-bold">{replyingTo.userDisplayName}</span></span>
                                    <button type="button" onClick={() => setReplyingTo(null)} className="text-gray-400 hover:text-white p-1">
                                        <i className="ph ph-x"></i>
                                    </button>
                                </div>
                            )}
                            <input
                                type="text"
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                onPaste={(e) => {
                                    const items = e.clipboardData?.items;
                                    if (items) {
                                        for (let i = 0; i < items.length; i++) {
                                            if (items[i].type.indexOf('image') !== -1) {
                                                e.preventDefault();
                                                alert("Please use the GIF button next to the text box to send GIFs.");
                                                return;
                                            }
                                        }
                                    }
                                }}
                                placeholder={replyingTo ? "Write a reply..." : "Add a comment..."}
                                className="w-full bg-transparent px-4 py-3 text-sm focus:outline-none placeholder-gray-400 text-white"
                            />
                        </div>
                        <button type="submit" disabled={!newComment.trim()} className="text-white font-bold p-2 rounded-full text-xl disabled:text-gray-500">
                            <i className="ph-duotone ph-paper-plane-tilt"></i>
                        </button>
                    </form>
                </div>
            )}

            <div className="space-y-4 max-h-48 overflow-y-auto custom-scrollbar pr-2 pb-4">
                {initialComments.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-2">No comments yet.</p>
                ) : (
                    (() => {
                        const topLevelComments = initialComments.filter(c => !c.replyToId).sort((a,b) => a.createdAt.toMillis() - b.createdAt.toMillis());
                        
                        const renderComment = (comment: Comment, isReply: boolean = false) => {
                            const isOwnComment = comment.userId === userProfile?.uid;
                            const isPostCreator = comment.userId === creatorId;
                            const commenterProfile = allUsers.find(u => u.uid === comment.userId);
                            const hasReactions = comment.reactions && Object.keys(comment.reactions).length > 0;
                            const isInteracting = activeComment?.id === comment.id;
                            
                            let displayText = comment.text;
                            const spoilerDetected = isContentSpoiler(comment.text);
                            
                            if (spoilerDetected && !isOwnComment && !showSpoilers) {
                                displayText = "****";
                            }

                            return (
                                <div key={comment.id} className="flex flex-col mt-3">
                                    <div className="flex items-start space-x-2 text-sm group">
                                        <div className="relative flex-shrink-0">
                                            <img src={comment.userPhotoURL} alt={comment.userDisplayName} className={`rounded-full object-cover ${isReply ? 'w-6 h-6' : 'w-8 h-8'}`} />
                                            {isUserOnline(commenterProfile) && (
                                                <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white/50" title="Online"></span>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div 
                                                className={`bg-white/10 rounded-lg p-2 relative transition-all duration-200 select-none ${isInteracting ? 'bg-white/20 scale-105 z-10 shadow-lg' : ''}`}
                                                style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
                                                onTouchStart={(e) => handlePressStart(e, comment)}
                                                onTouchEnd={handlePressEnd}
                                                onMouseDown={(e) => handlePressStart(e, comment)}
                                                onMouseUp={handlePressEnd}
                                                onMouseLeave={handlePressEnd}
                                                onContextMenu={(e) => { e.preventDefault(); handlePressStart(e, comment);}}
                                            >
                                                <p className="font-bold text-white flex items-center gap-2">
                                                    {comment.userDisplayName}
                                                    {isPostCreator && <span className="text-[10px] font-normal bg-purple-500 px-1.5 py-0.5 rounded-full">Creator</span>}
                                                </p>
                                                {comment.imageUrl && (
                                                    <div className="mt-1 mb-2 rounded-lg overflow-hidden max-w-[150px]">
                                                        <img src={comment.imageUrl} alt="GIF" className="w-full h-auto" referrerPolicy="no-referrer" />
                                                    </div>
                                                )}
                                                {displayText && <TaggedContent baseClassName="text-gray-200" text={displayText} userId={comment.userId} />}
                                                
                                                {hasReactions && (
                                                     <div className="absolute -bottom-3 left-2 flex items-center gap-1 z-10">
                                                        {Object.entries(comment.reactions!).map(([emoji, uids]) => (
                                                            Array.isArray(uids) && uids.length > 0 && (
                                                                <div key={emoji} className="bg-purple-800 border border-purple-600 rounded-full px-2 py-0.5 flex items-center text-[10px] shadow-md">
                                                                    <span>{emoji}</span>
                                                                    <span className="ml-1 font-semibold text-gray-200">{uids.length}</span>
                                                                </div>
                                                            )
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-4 mt-1 pl-1">
                                                <p className="text-[11px] text-gray-400">{comment.createdAt.toDate().toLocaleDateString()}</p>
                                                <button 
                                                    onClick={() => setReplyingTo(comment)} 
                                                    className="text-[11px] font-bold text-gray-400 hover:text-white transition-colors cursor-pointer"
                                                >
                                                    Reply
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {!isReply && (() => {
                                        const replies = initialComments.filter(c => c.replyToId === comment.id).sort((a,b) => a.createdAt.toMillis() - b.createdAt.toMillis());
                                        if (replies.length > 0) {
                                            return (
                                                <div className="ml-10 space-y-1">
                                                    {replies.map(reply => renderComment(reply, true))}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            );
                        };

                        return topLevelComments.map(c => renderComment(c, false));
                    })()
                )}
            </div>
            
            <InteractionMenu 
                isOpen={!!activeComment && !!menuPosition}
                onClose={() => { setActiveComment(null); setMenuPosition(null); }}
                position={menuPosition || { top: 0, left: 0, width: 0, height: 0 }}
                onReaction={handleReaction}
                onAction={handleAction}
                isOwner={activeComment?.userId === userProfile?.uid}
                isAdmin={userProfile?.isAdmin}
                type="comment"
            />

            {showGiphy && (
                <GiphyPicker 
                    onSelect={(url) => handleSubmitComment(undefined, url)}
                    onClose={() => setShowGiphy(false)}
                />
            )}
        </div>
    );
};

export default CommentSection;
